import { Fragment, useEffect, useState } from 'react';
import { Container } from '@/components/container';
import {
    Toolbar,
    ToolbarActions,
    ToolbarDescription,
    ToolbarHeading,
    ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { CompaniesContent } from './CompaniesContent';
import { CompanyModal } from '@/pages/buddy/company/blocks';
import { KeenIcon } from '@/components';
import axios from 'axios';
import { CompanyDetailsData } from '@/pages/buddy/types/CompanyDetailsData';
import { useParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { NoCompaniesContent } from './NoCompaniesContent';

const CompaniesPage = () => {
    const [companies, setCompanies] = useState<CompanyDetailsData[]>([]);
    const [CompanyModalOpen, setCompanyModalOpen] = useState(false);
    const handleOpen = () => setCompanyModalOpen(true);
    const handleClose = () => setCompanyModalOpen(false);
    const { tab } = useParams<{ tab?: string }>();
    const [activePage, setActivePage] = useState<string>(tab || 'overview');

    const [loading, setLoading] = useState(true);

    const API_COMPANIES = `${import.meta.env.VITE_APP_API_URL}/companies`;
    const API_COMPANY = `${import.meta.env.VITE_APP_API_URL}/company`;

    const fetchCompanyData = async () => {
        setLoading(true);
        try {
            const response = await axios.get<CompanyDetailsData[]>(API_COMPANIES, {
                headers: {
                    Accept: 'application/json',
                },
            });
            setCompanies(response.data);

            if (response.data.length === 0) {
                handleOpen();
            }
        } catch (err) {
            enqueueSnackbar(`Failed to fetch companies data: ${err}`, {
                variant: 'solid',
                state: 'danger',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDeleteCompany = async (companyId: string) => {
        try {
            await axios.delete(`${API_COMPANY}/${companyId}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            fetchCompanyData();
            enqueueSnackbar('The company has been successfully deleted.', {
                variant: 'solid',
                state: 'success',
            });
        } catch (err) {
            enqueueSnackbar(`Failed to delete the company: ${err}`, {
                variant: 'solid',
                state: 'danger',
            });
        }
    };

    useEffect(() => {
        fetchCompanyData();
    }, []);

    return (
        <Fragment>
            <PageNavbar />

            <CompanyModal mode="create" open={CompanyModalOpen} onClose={handleClose} />

            <Container>
                <Toolbar>
                    <ToolbarHeading>
                        <ToolbarPageTitle />
                        <ToolbarDescription>
                            View all companies to organize and manage their jobs, simplifying the process of sorting and
                            matching candidates to specific roles.
                        </ToolbarDescription>
                    </ToolbarHeading>
                    <ToolbarActions>
                        <button
                            onClick={handleOpen}
                            type="button"
                            className={`btn btn-sm btn-primary ${companies.length === 0 ? 'pulsating' : ''}`}
                        >
                            <KeenIcon icon="plus"/> Add New Company Profile
                        </button>
                    </ToolbarActions>
                </Toolbar>
            </Container>

            <Container>
                {loading ? (
                    <div>Loading...</div>
                ) : companies.length === 0 ? (
                    <NoCompaniesContent onAddNewCompany={handleOpen}/>
                ) : (
                    <CompaniesContent
                        companies={companies}
                        loading={loading}
                        on_delete={handleConfirmDeleteCompany}
                    />
                )}
            </Container>
        </Fragment>
    );
};

export { CompaniesPage };
